import { LitElement, html, css } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// The merged conversation timeline (§3.5): public replies, agent-only internal
// notes, and system event lines. Comment bodies are server-sanitized HTML,
// rendered via unsafeHTML; the .body styles here cover the tags the editor emits.
// Restyled to the BrandExtract comment card (avatar, role tag, internal tint).
class TicketThread extends LitElement {
  static properties = { comments: { type: Array } }

  static styles = css`
    /* minmax(0, …): a grid item's automatic minimum is its min-content width, so an
       unbreakable string in a comment body would widen this track and scroll the page
       sideways (same reason as page-ticket.js). The body then wraps rather than
       scrolling — a comment you are reading should not need horizontal scrolling. */
    :host { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; font-family: var(--font-sans); }
    .comment { background: var(--white); border-radius: var(--radius-card); padding: 24px; box-shadow: var(--shadow-row); }
    .comment.internal { background: var(--chartreuse-bg); box-shadow: inset 0 0 0 1px var(--chartreuse-300); }
    .head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
    .avatar { width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0; display: grid; place-items: center;
              font-weight: 700; font-size: 14px; }
    .avatar.eggplant { background: var(--eggplant-100); color: var(--eggplant-500); }
    .avatar.sky { background: var(--sky-100); color: var(--sky-700); }
    .meta .line { display: flex; align-items: center; gap: 10px; }
    .author { font-weight: 700; font-size: 15px; color: var(--eggplant-500); }
    .role { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
            text-transform: uppercase; color: var(--neutral-500); }
    .role.note { color: var(--chartreuse-700); }
    .when { font-size: 12.5px; color: var(--neutral-500); margin-top: 2px; }
    .event { text-align: center; color: var(--neutral-500); font-size: 13px; padding: 2px 0;
             font-family: var(--font-eyebrow); }
    .event .dot { opacity: 0.5; margin: 0 0.4rem; }

    /* Rendered rich-text (matches the tags rich-editor / Quill emit) */
    .body { color: var(--neutral-800); font-size: 15px; line-height: 1.6; }
    .body > :first-child { margin-top: 0; }
    .body > :last-child { margin-bottom: 0; }
    .body p { margin: 0 0 0.6em; }
    .body h1, .body h2, .body h3 { color: var(--eggplant-500); margin: 0.5em 0 0.3em; line-height: 1.25; }
    .body h2 { font-size: 1.15rem; } .body h3 { font-size: 1.02rem; }
    .body a { color: var(--sky-500); }
    .body ul, .body ol { padding-left: 1.5em; margin: 0.4em 0; }
    .body ul { list-style: disc; } .body ol { list-style: decimal; }
    .body li[data-list="bullet"] { list-style-type: disc; }
    .body li[data-list="ordered"] { list-style-type: decimal; }
    .body blockquote { border-left: 3px solid var(--chartreuse-500); margin: 0.5em 0; padding-left: 0.8em; color: var(--neutral-600); }
    .body pre { background: var(--neutral-50); padding: 0.6em; border-radius: 5px; overflow-x: auto; font-size: 0.85em; }
    .body img { max-width: 100%; height: auto; border-radius: 5px; margin: 0.3em 0; display: block; }
    .body { overflow-wrap: break-word; }
    .body table { display: block; max-width: 100%; overflow-x: auto; }  /* emailed tables scroll, not the page */

    .attachments { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.7rem; }
    .att { display: inline-flex; align-items: center; gap: 0.4rem; text-decoration: none; font-size: 13px;
           background: var(--neutral-50); box-shadow: inset 0 0 0 1px var(--border-input); border-radius: 5px;
           padding: 0.3rem 0.6rem; color: var(--eggplant-500); }
    .att:hover { box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    .att .sz { color: var(--neutral-500); }
  `

  _when(iso) {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  }

  _initials(name) {
    return (name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
  }

  _renderItem(c) {
    if (c.kind === 'event') {
      return html`<div class="event">${c.body}<span class="dot">·</span>${this._when(c.createdAt)}</div>`
    }
    const author = c.author ? (c.author.name || c.author.email) : 'System'
    const tone = c.author?.role === 'client' ? 'sky' : 'eggplant'
    return html`
      <div class="comment ${c.kind}">
        <div class="head">
          <span class="avatar ${tone}">${this._initials(author)}</span>
          <div class="meta">
            <div class="line">
              <span class="author">${author}</span>
              ${c.kind === 'internal'
                ? html`<span class="role note">Internal note</span>`
                : (c.author ? html`<span class="role">${c.author.role}</span>` : '')}
            </div>
            <div class="when">${this._when(c.createdAt)}</div>
          </div>
        </div>
        <div class="body">${unsafeHTML(c.body)}</div>
        ${c.attachments?.length ? html`
          <div class="attachments">
            ${c.attachments.map(a => html`
              <a class="att" href=${a.url} target="_blank" rel="noopener">
                📎 ${a.filename} <span class="sz">${fmtSize(a.size)}</span>
              </a>`)}
          </div>` : ''}
      </div>
    `
  }

  render() {
    return html`${(this.comments || []).map(c => this._renderItem(c))}`
  }
}

customElements.define('ticket-thread', TicketThread)
